<script setup lang="ts">
import { computed } from 'vue';
import { usePermissions } from '@/composables/usePermissions';
import { type Requirement } from '@/api/planning/requirements';
import { type WeeklyPlan } from '@/api/planning/weekly-plans';
import {
  expectedVisitsInMonth, PERIOD_NOUNS_ES, roundExpectedVisits, WEEKDAY_NAMES_ES,
  WEEKDAY_INITIALS_ES, WEEKDAYS_MONDAY_FIRST, weekdaySortKey,
} from '@/utils/monthly-expectation';
import { careerMetadata } from '@/utils/career-metadata';

interface Props {
  requirement: Requirement;
  currentYear: number;
  currentMonth: number;
  currentMonthName: string;
}

const props = defineProps<Props>();

defineEmits<{
  (e: 'edit', req: Requirement): void;
  (e: 'delete', req: Requirement): void;
  (e: 'add-plan', req: Requirement): void;
  (e: 'edit-plan', req: Requirement, plan: WeeklyPlan): void;
  (e: 'delete-plan', plan: WeeklyPlan): void;
}>();

const { hasPermission } = usePermissions();

const cap = computed(() => roundExpectedVisits(expectedVisitsInMonth(
  props.requirement, { year: props.currentYear, month: props.currentMonth },
)));

const isWeekly = computed(() => props.requirement.frequency === 'weekly');
const periodNoun = computed(() => PERIOD_NOUNS_ES[props.requirement.frequency]);
const visitsLabel = computed(() => {
  const { visitsPerPeriod } = props.requirement;
  return `${visitsPerPeriod} ${visitsPerPeriod === 1 ? 'turno' : 'turnos'}/${periodNoun.value}`;
});

const planSum = computed(() => props.requirement.weeklyPlans.reduce(
  (acc, p) => acc + p.days.length, 0,
));

function dayCircleColor(plan: WeeklyPlan, day: number): string {
  return plan.days.includes(day) ? 'primary' : 'grey-lighten-3';
}

function dayCircleTextColor(plan: WeeklyPlan, day: number): string {
  return plan.days.includes(day) ? 'white' : 'grey-darken-1';
}

function formatPlanDays(days: number[]): string {
  return [...days]
    .sort((a, b) => weekdaySortKey(a) - weekdaySortKey(b))
    .map((d) => WEEKDAY_NAMES_ES[d])
    .join(' · ');
}

function canModifyCareer(career: string): boolean {
  return hasPermission.value('careers', `read_${career}`);
}

const canWrite = computed(() => hasPermission.value('requirements', 'create') ||
                                hasPermission.value('requirements', 'update'));
</script>

<template>
  <v-card
    class="mb-4 requirement-card"
    rounded="lg"
    elevation="1"
  >
    <v-card-text class="pa-4">
      <!-- Card header -->
      <div class="d-flex align-center ga-3 flex-wrap">
        <v-avatar
          :color="careerMetadata(requirement.career).color"
          size="44"
          variant="tonal"
        >
          <v-icon
            :icon="careerMetadata(requirement.career).icon"
            size="24"
          />
        </v-avatar>
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-bold">
            {{ requirement.humanCareer }}
          </div>
          <div class="text-body-2 text-medium-emphasis">
            Desde {{ requirement.humanStartsOn || requirement.startsOn }}
            <span v-if="requirement.endsOn">
              · hasta {{ requirement.humanEndsOn || requirement.endsOn }}
            </span>
          </div>
        </div>
        <div class="d-flex ga-2 flex-wrap">
          <v-chip
            v-if="requirement.skillName"
            color="deep-purple"
            variant="tonal"
            prepend-icon="mdi-certificate-outline"
          >
            {{ requirement.skillName }}
          </v-chip>
          <v-chip
            color="primary"
            variant="tonal"
            :prepend-icon="isWeekly ? 'mdi-calendar-week' : 'mdi-calendar-month'"
          >
            {{ visitsLabel }}
          </v-chip>
          <v-chip
            variant="tonal"
            prepend-icon="mdi-timer-outline"
          >
            {{ requirement.durationMin }} min
          </v-chip>
          <v-chip
            color="success"
            variant="tonal"
            prepend-icon="mdi-target"
          >
            {{ cap }} en {{ currentMonthName }}
          </v-chip>
        </div>
        <div
          v-if="canWrite && canModifyCareer(requirement.career)"
          class="d-flex ga-1"
        >
          <v-btn
            v-if="hasPermission('requirements', 'update')"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="$emit('edit', requirement)"
          >
            <v-icon />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Editar
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="hasPermission('requirements', 'destroy')"
            icon="mdi-delete-outline"
            size="small"
            variant="text"
            color="error"
            @click="$emit('delete', requirement)"
          >
            <v-icon />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Eliminar
            </v-tooltip>
          </v-btn>
        </div>
      </div>

      <!-- Sum / agreed warning -->
      <v-alert
        v-if="isWeekly && planSum !== requirement.visitsPerPeriod && requirement.weeklyPlans.length > 0"
        type="warning"
        variant="tonal"
        density="compact"
        class="mt-3"
        icon="mdi-alert-outline"
      >
        Presupuestado: <strong>{{ planSum }}</strong>
        / Acordado: <strong>{{ requirement.visitsPerPeriod }}</strong>
      </v-alert>

      <v-divider class="my-4" />

      <!-- Biweekly and monthly visits are scheduled by hand from the calendar -->
      <v-alert
        v-if="!isWeekly"
        type="info"
        variant="tonal"
        density="compact"
        icon="mdi-calendar-month"
      >
        Requerimiento {{ requirement.humanFrequency.toLowerCase() }}: los turnos se agendan
        manualmente desde el calendario y cuentan en el presupuesto del mes.
      </v-alert>

      <!-- Weekly plans section -->
      <div
        v-else
        class="d-flex align-center justify-space-between mb-3"
      >
        <div class="text-subtitle-2 font-weight-bold">
          <v-icon
            icon="mdi-calendar-week-begin"
            size="small"
            class="mr-1"
          />
          Planes semanales
        </div>
        <v-btn
          v-if="hasPermission('weekly_plans', 'create') && canModifyCareer(requirement.career)"
          size="small"
          variant="tonal"
          color="primary"
          prepend-icon="mdi-plus"
          text="Añadir plan"
          @click="$emit('add-plan', requirement)"
        />
      </div>

      <p
        v-if="isWeekly && requirement.weeklyPlans.length === 0"
        class="text-body-2 text-medium-emphasis text-center py-4 mb-0"
      >
        Sin planes semanales. Añade uno para definir los días y horarios.
      </p>

      <div
        v-for="plan in requirement.weeklyPlans"
        :key="plan.id"
        class="d-flex align-center ga-3 py-2 plan-row"
      >
        <!-- Day circles -->
        <div class="d-flex ga-1">
          <v-avatar
            v-for="day in WEEKDAYS_MONDAY_FIRST"
            :key="day"
            :color="dayCircleColor(plan, day)"
            size="28"
          >
            <span
              class="text-caption font-weight-bold"
              :class="`text-${dayCircleTextColor(plan, day)}`"
            >
              {{ WEEKDAY_INITIALS_ES[day] ?? '' }}
            </span>
          </v-avatar>
        </div>

        <div class="flex-grow-1 d-flex align-center ga-2 flex-wrap">
          <v-chip
            v-if="plan.fixedStartTime"
            size="small"
            color="info"
            variant="tonal"
            prepend-icon="mdi-clock"
          >
            {{ plan.fixedStartTime }}
          </v-chip>
          <v-chip
            v-else-if="plan.timeWindowStart && plan.timeWindowEnd"
            size="small"
            color="info"
            variant="tonal"
            prepend-icon="mdi-clock-outline"
          >
            {{ plan.timeWindowStart }}–{{ plan.timeWindowEnd }}
          </v-chip>
          <v-chip
            v-if="plan.caregiverFullName"
            size="small"
            variant="tonal"
            prepend-icon="mdi-account"
          >
            {{ plan.caregiverFullName }}
          </v-chip>
          <span class="text-body-2 text-medium-emphasis">
            {{ formatPlanDays(plan.days) }}
          </span>
        </div>

        <div
          v-if="canModifyCareer(requirement.career)"
          class="d-flex ga-1"
        >
          <v-btn
            v-if="hasPermission('weekly_plans', 'update')"
            icon="mdi-pencil"
            size="x-small"
            variant="text"
            @click="$emit('edit-plan', requirement, plan)"
          />
          <v-btn
            v-if="hasPermission('weekly_plans', 'destroy')"
            icon="mdi-delete-outline"
            size="x-small"
            variant="text"
            color="error"
            @click="$emit('delete-plan', plan)"
          />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.requirement-card {
  transition: box-shadow .2s ease;
}

.requirement-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, .08) !important;
}

.plan-row {
  border-radius: 8px;
  transition: background-color .15s ease;
}

.plan-row:hover {
  background-color: rgba(var(--v-theme-surface-variant), .5);
}
</style>
